import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Star, CheckCircle2, MessageSquare } from 'lucide-react';

export const PublicReviews = ({ businessLocation, limit = 10, showGoogleReviews = true }) => {
  const [reviews, setReviews] = useState([]);
  const [googleReviews, setGoogleReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        // Fetch website reviews
        const websiteUrl = businessLocation
          ? `${backendUrl}/api/reviews/public?business_location=${businessLocation}&limit=${limit}`
          : `${backendUrl}/api/reviews/public?limit=${limit}`;
        
        const websiteRes = await fetch(websiteUrl);
        if (websiteRes.ok) {
          const data = await websiteRes.json();
          setReviews(data.reviews || []);
        }

        // Fetch Google reviews if enabled
        if (showGoogleReviews) {
          const googleRes = await fetch(`${backendUrl}/api/reviews/google?limit=${limit}`);
          if (googleRes.ok) {
            const googleData = await googleRes.json();
            setGoogleReviews(googleData.reviews || []);
          }
        }
      } catch (error) {
        console.log('Could not fetch reviews');
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [businessLocation, limit, backendUrl, showGoogleReviews]);

  if (loading) {
    return <div className="text-center py-8">Loading reviews...</div>;
  }

  const allReviews = [...googleReviews, ...reviews];

  if (allReviews.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No reviews yet. Be the first to leave a review!</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Google Reviews Section */}
      {googleReviews.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <img 
              src="https://www.google.com/favicon.ico" 
              alt="Google" 
              className="w-5 h-5"
            />
            <span className="font-semibold text-lg">Google Reviews</span>
            <Badge variant="outline" className="ml-2 bg-green-50 text-green-700 border-green-300">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Verified
            </Badge>
          </div>
          
          {googleReviews.map((review, index) => (
            <Card key={`google-${index}`} className="p-6 border-l-4 border-l-amber-400 bg-gradient-to-r from-amber-50/50 to-transparent">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-bold text-lg">{review.reviewer_name}</h4>
                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                      Google
                    </Badge>
                  </div>
                  <div className="flex gap-1 mt-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${
                          i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <span className="text-sm text-muted-foreground">
                  {review.time_ago}
                </span>
              </div>
              <p className="text-muted-foreground leading-relaxed">{review.review_text}</p>
              
              {/* Owner Response */}
              {review.owner_response && (
                <div className="mt-4 pl-4 border-l-2 border-amber-300 bg-amber-50/50 p-3 rounded-r">
                  <div className="flex items-center gap-2 mb-1">
                    <MessageSquare className="w-4 h-4 text-amber-600" />
                    <span className="font-semibold text-sm text-amber-800">Response from Eastend</span>
                    {review.owner_response_time && (
                      <span className="text-xs text-muted-foreground">• {review.owner_response_time}</span>
                    )}
                  </div>
                  <p className="text-sm text-amber-900">{review.owner_response}</p>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Website Reviews Section */}
      {reviews.length > 0 && (
        <div className="space-y-4">
          {googleReviews.length > 0 && (
            <div className="flex items-center gap-2 mb-4 mt-8">
              <span className="font-semibold text-lg">Recent Website Reviews</span>
            </div>
          )}
          
          {reviews.map((review) => (
            <Card key={review.review_id} className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="font-bold text-lg">{review.customer_name}</h4>
                  <div className="flex gap-1 mt-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${
                          i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <span className="text-sm text-muted-foreground">
                  {new Date(review.created_at).toLocaleDateString()}
                </span>
              </div>
              <p className="text-muted-foreground leading-relaxed">{review.review_text}</p>
              {review.was_updated && (
                <p className="text-xs text-green-600 mt-2 italic">✓ Updated review</p>
              )}
            </Card>
          ))}
        </div>
      )}
      
      {/* SEO fallback */}
      <noscript>
        <div>
          <h3>Customer Reviews:</h3>
          {allReviews.map((review, index) => (
            <div key={index} style={{marginBottom: '20px', padding: '15px', border: '1px solid #ccc'}}>
              <strong>{review.reviewer_name || review.customer_name}</strong> - {review.rating}/5 stars
              <p>{review.review_text}</p>
              <small>{review.time_ago || (review.created_at && new Date(review.created_at).toLocaleDateString())}</small>
              {review.owner_response && (
                <div style={{marginTop: '10px', paddingLeft: '15px', borderLeft: '2px solid #f59e0b'}}>
                  <strong>Response from Eastend:</strong>
                  <p>{review.owner_response}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </noscript>
    </div>
  );
};
